// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricEntity, BaseMetricConfig, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for MeetupAttendance entity
 */
type MeetupAttendanceConfig = BaseMetricConfig;

/**
 * MeetupAttendance Entity - Encapsulates all meetup attendance-specific business logic
 *
 * This class consolidates configuration, scoring rules, and display logic
 * for the meetup attendance ecosystem metric.
 */
export class MeetupAttendance extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'meetup_attendance' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: MeetupAttendanceConfig = {
    metricType: 'meetup_attendance',
    display: {
      displayName: 'Meetup Attendance',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of local meetups do employees from our company attend?',
        scoringRules: [
          '0 meetups: = 0 points',
          '0.1 to 25% of meetups = 0.33 points',
          '25.1 to 50% of meetups = 0.66 points',
          '>50% of meetups = 1 point',
          '0 points = <b>Silent</b>',
          '0.33 points = <b>Participating</b>',
          '0.66 points = <b>Contributing</b>',
          '1 point = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: `Our company has no meetup attendance for {context}.`,
      },
      positiveValue: {
        descriptionTemplate: `Employees from our company attended {percentage}% of all meetups for {context}.`,
      },
      nullValue: {
        description: this.NULL_VALUE_DESCRIPTION,
      }
    }
  };

  /**
   * Get display configuration from config object
  */
  public static getDisplayConfig(): BaseMetricConfig['display'] {
      return this.CONFIG.display;
  }

  /**
   * Get full configuration object
   */
  public static getConfig(): BaseMetricConfig {
      return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for meetup attendance
   */
  static getBottomSummary(percentage: string, allTimeTotal: number, isLfProject: boolean, level: ScoreLevel, projectType?: string | null): MetricBottomSummary {
    if (isLfProject === false) {
      return this.getNonLfProjectBottomSummary(this.CONFIG);
    }

    if (allTimeTotal === 0) {
      return this.getNullValueBottomSummary(this.CONFIG);
    }

    const percentageValue = percentage || '0.0';
    const numericValue = parseFloat(percentageValue);
    const styles = this.getStylesByLevel(level);
    const contextText = projectType?.toLowerCase() === 'foundation'
      ? 'this foundation'
      : 'this project\'s foundation';

    if (numericValue === 0) {
      const description = this.formatDescription(
        this.CONFIG.bottomSummary.zeroValue.description,
        { context: contextText }
      );

      return {
        title: this.CONFIG.display.displayName,
        description,
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    const description = this.formatDescription(
      this.CONFIG.bottomSummary.positiveValue.descriptionTemplate,
      {
        percentage: percentageValue,
        context: contextText
      }
    );

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }


}
